"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CaptureFlowTelemetryRegistry = exports.CaptureFlowSession = exports.CAPTURE_FLOW_TELEMETRY_MAX_ENTRIES = exports.CAPTURE_RUN_BREAK_MS = void 0;
exports.resolveCapturedAt = resolveCapturedAt;
exports.keepStoredLastModified = keepStoredLastModified;
exports.hasGalleryCaptureSignals = hasGalleryCaptureSignals;
exports.telemetryPhotoKey = telemetryPhotoKey;
exports.slimCaptureTelemetrySource = slimCaptureTelemetrySource;
exports.computeNextCaptureSignals = computeNextCaptureSignals;
exports.persistableCaptureSignals = persistableCaptureSignals;
exports.captureFlowTelemetryProps = captureFlowTelemetryProps;
/** Max gap (ms) that still belongs to the same uninterrupted capture run. */
exports.CAPTURE_RUN_BREAK_MS = 90_000;
/** Max remembered capture-signal entries (LRU). Photo keys plus classify sessions. */
exports.CAPTURE_FLOW_TELEMETRY_MAX_ENTRIES = 200;
/**
 * Clock value for gap/run computation (and telemetry fallbacks).
 * Does **not** rewrite a stored `lastModified` of `0` on the image — keep the
 * stored value and only use this fallback when a positive timestamp is required.
 */
function resolveCapturedAt(lastModified, now = Date.now()) {
    return typeof lastModified === 'number' && Number.isFinite(lastModified) && lastModified > 0
        ? lastModified
        : now;
}
/** Keep a stored lastModified even when it is 0. Use `resolveCapturedAt` only for clocks. */
function keepStoredLastModified(lastModified, fallbackNow) {
    return typeof lastModified === 'number' && Number.isFinite(lastModified) ? lastModified : fallbackNow;
}
function hasGalleryCaptureSignals(image) {
    return image != null && (image.runIndex != null || image.gapFromPreviousMs !== undefined);
}
/**
 * Stable lookup key for a photo. Prefer `storageId` / `photoKey` / `id`.
 * The `lm:` fallback is last-write-wins when two photos share the same
 * lastModified and have no other identity — do not rely on it for uniqueness.
 */
function telemetryPhotoKey(source) {
    if (!source)
        return undefined;
    if (source.storageId)
        return `storage:${source.storageId}`;
    if (source.photoKey)
        return `photo:${source.photoKey}`;
    if (source.id != null && source.id !== '')
        return `id:${source.id}`;
    if (typeof source.lastModified === 'number' && Number.isFinite(source.lastModified)) {
        return `lm:${source.lastModified}`;
    }
    return undefined;
}
function slimCaptureTelemetrySource(source) {
    const slim = {};
    if (typeof source.lastModified === 'number' && Number.isFinite(source.lastModified)) {
        slim.lastModified = source.lastModified;
    }
    if (source.gapFromPreviousMs !== undefined) {
        slim.gapFromPreviousMs = source.gapFromPreviousMs;
    }
    if (source.runIndex != null) {
        slim.runIndex = source.runIndex;
    }
    return slim;
}
function lookupCandidateKeys(key) {
    if (key.startsWith('storage:') ||
        key.startsWith('photo:') ||
        key.startsWith('id:') ||
        key.startsWith('lm:')) {
        return [key];
    }
    // Callers (RN journey image_add) pass a raw storageId. Accept that and the
    // prefixed forms so `propsFor('abc')` finds `storage:abc`.
    return [key, `storage:${key}`, `photo:${key}`, `id:${key}`];
}
function computeNextCaptureSignals(capturedAt, state, sessionId, maxGapMs = exports.CAPTURE_RUN_BREAK_MS) {
    const rawGap = state.lastCapturedAt == null ? null : capturedAt - state.lastCapturedAt;
    // A negative gap is a run break (clock skew, out-of-order stamps, or a
    // later-selected library asset). Do not keep the run alive.
    const gapFromPreviousMs = rawGap != null && rawGap < 0 ? null : rawGap;
    const sameRun = state.lastCapturedAt != null &&
        state.lastSessionId === sessionId &&
        rawGap != null &&
        rawGap >= 0 &&
        rawGap <= maxGapMs;
    const runIndex = sameRun ? state.lastRunIndex + 1 : 1;
    return {
        signals: {
            capturedAt,
            gapFromPreviousMs,
            runIndex,
        },
        nextState: {
            lastCapturedAt: capturedAt,
            lastRunIndex: runIndex,
            lastSessionId: sessionId,
        },
    };
}
class CaptureFlowSession {
    state = {
        lastCapturedAt: null,
        lastRunIndex: 0,
        lastSessionId: null,
    };
    sessionId = null;
    nextSessionId = 1;
    reportId = undefined;
    beginSession() {
        this.sessionId = this.nextSessionId;
        this.nextSessionId += 1;
        this.state = {
            lastCapturedAt: null,
            lastRunIndex: 0,
            lastSessionId: this.sessionId,
        };
        return this.sessionId;
    }
    /**
     * Drop the previous report's last photo time. A new report or a switch of
     * report must not inherit `lastCapturedAt` from another report.
     */
    resetForReport(reportId) {
        if (this.reportId === reportId && this.reportId !== undefined)
            return;
        this.reportId = reportId ?? null;
        this.state = {
            lastCapturedAt: null,
            lastRunIndex: 0,
            lastSessionId: null,
        };
        this.sessionId = null;
    }
    record(capturedAt, maxGapMs = exports.CAPTURE_RUN_BREAK_MS) {
        const sessionId = this.sessionId ?? this.beginSession();
        const { signals, nextState } = computeNextCaptureSignals(capturedAt, this.state, sessionId, maxGapMs);
        this.state = nextState;
        return signals;
    }
    getState() {
        return this.state;
    }
}
exports.CaptureFlowSession = CaptureFlowSession;
/**
 * Per-photo (or per classify-session) telemetry lookup.
 * Stores only lastModified / gap / runIndex (plain numbers) so gallery items
 * with base64 or AI subscriptions are not retained. Bounded LRU.
 */
class CaptureFlowTelemetryRegistry {
    maxEntries;
    byKey = new Map();
    pending = null;
    constructor(maxEntries = exports.CAPTURE_FLOW_TELEMETRY_MAX_ENTRIES) {
        this.maxEntries = maxEntries;
    }
    get size() {
        return this.byKey.size;
    }
    remember(source, key) {
        if (!source) {
            if (key)
                this.byKey.delete(key);
            else
                this.pending = null;
            return;
        }
        const resolved = key || telemetryPhotoKey(source);
        if (!resolved)
            return;
        this.put(resolved, slimCaptureTelemetrySource(source));
    }
    /** Remember the photo that the next classify session should bind to. */
    rememberPending(source) {
        if (!source) {
            this.pending = null;
            return;
        }
        this.remember(source);
        this.pending = slimCaptureTelemetrySource(source);
    }
    bindPendingToSession(sessionId) {
        if (!sessionId || !this.pending)
            return;
        this.put(sessionId, this.pending);
        this.pending = null;
    }
    propsFor(keyOrSource) {
        if (keyOrSource == null)
            return {};
        if (typeof keyOrSource === 'object')
            return captureFlowTelemetryProps(keyOrSource);
        return captureFlowTelemetryProps(this.take(keyOrSource));
    }
    get(key) {
        return this.take(key);
    }
    forget(key) {
        for (const candidate of lookupCandidateKeys(key)) {
            this.byKey.delete(candidate);
        }
    }
    clear() {
        this.byKey.clear();
        this.pending = null;
    }
    take(key) {
        const found = this.lookup(key);
        if (!found)
            return undefined;
        this.byKey.delete(found.storedKey);
        this.byKey.set(found.storedKey, found.value);
        return found.value;
    }
    lookup(key) {
        for (const candidate of lookupCandidateKeys(key)) {
            const value = this.byKey.get(candidate);
            if (value)
                return { storedKey: candidate, value };
        }
        return undefined;
    }
    put(key, value) {
        if (this.byKey.has(key))
            this.byKey.delete(key);
        this.byKey.set(key, value);
        while (this.byKey.size > this.maxEntries) {
            const oldest = this.byKey.keys().next().value;
            if (oldest == null)
                break;
            this.byKey.delete(oldest);
        }
    }
}
exports.CaptureFlowTelemetryRegistry = CaptureFlowTelemetryRegistry;
function persistableCaptureSignals(signals) {
    return {
        lastModified: signals.capturedAt,
        gapFromPreviousMs: signals.gapFromPreviousMs,
        runIndex: signals.runIndex,
    };
}
function captureFlowTelemetryProps(source) {
    if (!source)
        return {};
    const props = {};
    // `captured_at` is intended for newly captured (camera) photos. Skip 0 / invalid
    // mtimes so a stored lastModified of 0 is not rewritten and a library file's
    // real mtime is not sent unless the caller stamped capture signals with a
    // positive capture clock.
    if (typeof source.lastModified === 'number' && Number.isFinite(source.lastModified) && source.lastModified > 0) {
        if (source.runIndex != null || source.gapFromPreviousMs !== undefined) {
            props.captured_at = source.lastModified;
        }
    }
    if (source.gapFromPreviousMs !== undefined) {
        props.gap_from_previous_ms = source.gapFromPreviousMs;
    }
    if (source.runIndex != null) {
        props.run_index = source.runIndex;
    }
    return props;
}
